import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plane,
  FileText,
  CheckCircle,
  Video,
  PlusCircle,
  Clock,
  Globe,
  ArrowRight,
  Upload,
  Eye,
  AlertCircle
} from 'lucide-react';
import { getUserVisaApplications } from '../../../api/educentialApi';

const VisaDashboard = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    submitted: 0,
    approved: 0,
    pending: 0
  });

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await getUserVisaApplications();
      if (response.success && response.data) {
        const apps = response.data;
        setApplications(apps);

        // Calculate stats
        setStats({
          total: apps.length,
          submitted: apps.filter(a => a.status === 'SUBMITTED' || a.status === 'UNDER_REVIEW').length,
          approved: apps.filter(a => a.status === 'APPROVED').length,
          pending: apps.filter(a => a.status === 'DRAFT').length
        });
      }
    } catch (err) {
      console.error('Error fetching visa applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      DRAFT: 'bg-gray-100 text-gray-800',
      SUBMITTED: 'bg-blue-100 text-blue-800',
      UNDER_REVIEW: 'bg-yellow-100 text-yellow-800',
      DOCUMENTS_REQUESTED: 'bg-orange-100 text-orange-800',
      APPROVED: 'bg-green-100 text-green-800',
      REJECTED: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const quickActions = [
    {
      title: 'Start New Application',
      description: 'Begin your visa application process',
      icon: PlusCircle,
      color: 'indigo',
      path: '/services/visa/application/new',
      action: () => navigate('/services/visa/application/new')
    },
    {
      title: 'Interview Preparation',
      description: 'Practice questions and get tips',
      icon: Video,
      color: 'purple',
      path: '/services/visa/interview-prep',
      action: () => navigate('/services/visa/interview-prep')
    }
  ];

  const features = [
    {
      title: 'Comprehensive Application Form',
      description: '7-step guided visa application process',
      icon: FileText,
      color: 'teal'
    },
    {
      title: 'Document Management',
      description: 'Upload and track required documents',
      icon: Upload,
      color: 'blue'
    },
    {
      title: 'Status Tracking',
      description: 'Monitor your application progress',
      icon: Eye,
      color: 'green'
    },
    {
      title: 'Interview Resources',
      description: 'Prepare with common questions & tips',
      icon: Video,
      color: 'purple'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Visa Assistance</h1>
              <p className="text-gray-600 mt-1">Complete visa application support and guidance for your study abroad journey</p>
            </div>
            <Link
              to="/services"
              className="px-4 py-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
            >
              ← Back to Services
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Applications</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</p>
              </div>
              <div className="bg-indigo-100 p-3 rounded-lg">
                <FileText className="h-8 w-8 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Under Review</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.submitted}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-lg">
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Approved</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.approved}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Draft</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.pending}</p>
              </div>
              <div className="bg-gray-100 p-3 rounded-lg">
                <Globe className="h-8 w-8 text-gray-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <button
                  key={index}
                  onClick={action.action}
                  className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 hover:border-indigo-200 text-left"
                >
                  <div className={`bg-${action.color}-100 p-3 rounded-lg w-fit mb-4 group-hover:bg-${action.color}-600 transition-colors`}>
                    <Icon className={`w-6 h-6 text-${action.color}-600 group-hover:text-white transition-colors`} />
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900 group-hover:text-indigo-600 transition-colors">
                    {action.title}
                  </h3>
                  <p className="text-sm text-gray-600 mt-2">{action.description}</p>
                  <div className="flex items-center mt-4 text-indigo-600 group-hover:text-indigo-700">
                    <span className="text-sm font-medium">Get Started</span>
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Features Overview */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">What We Offer</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow"
                >
                  <div className={`bg-${feature.color}-100 p-3 rounded-lg w-fit mb-4`}>
                    <Icon className={`w-6 h-6 text-${feature.color}-600`} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Applications */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">My Visa Applications</h2>
            {applications.length > 0 && (
              <button
                onClick={() => navigate('/services/visa/application/new')}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium flex items-center"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                New Application
              </button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading applications...</p>
            </div>
          ) : applications.length > 0 ? (
            <div className="space-y-4">
              {applications.map((app) => (
                <div
                  key={app.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-semibold text-gray-900">
                          {app.country} - {app.visaType}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(app.status)}`}>
                          {app.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <FileText className="h-4 w-4 mr-1" />
                          <span>Ref: {app.applicationRef}</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          <span>Submitted: {formatDate(app.submittedAt)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {app.status === 'DRAFT' ? (
                        <button
                          onClick={() => navigate(`/services/visa/application/${app.id}`)}
                          className="px-4 py-2 text-sm border border-indigo-600 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
                        >
                          Continue
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => navigate(`/services/visa/application/${app.id}/status`)}
                            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                          >
                            View Status
                          </button>
                          <button
                            onClick={() => navigate(`/services/visa/application/${app.id}/documents`)}
                            className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            Documents
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="bg-gray-100 p-6 rounded-full w-fit mx-auto mb-4">
                <Plane className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Applications Yet</h3>
              <p className="text-gray-600 mb-6">Start your visa application process today</p>
              <button
                onClick={() => navigate('/services/visa/application/new')}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium inline-flex items-center"
              >
                <PlusCircle className="h-5 w-5 mr-2" />
                Create New Application
              </button>
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-8 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-6">
          <div className="flex items-start">
            <AlertCircle className="h-6 w-6 text-indigo-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h3>
              <p className="text-gray-700 mb-4">
                Our visa assistance service guides you through every step of the application process.
                From document preparation to interview tips, we've got you covered.
              </p>
              <button
                onClick={() => navigate('/services/visa/interview-prep')}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
              >
                View Interview Preparation
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisaDashboard;
